import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { useCreateList } from '@aibees/elephant/hooks/labels/use-create-list';
import { usePageType } from '@aibees/elephant/hooks/use-page-type';
import { SearchType } from '@aibees/elephant/types';
import { LabelGrouping, LabelType } from '@aibees/elephant/types/label';
import { notificationIconMap } from '@aibees/elephant/utils';
import { FormOnSubmit } from '@aibees/shared/components';
import { useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { YupFormData } from '@aibees/shared/utils/yup';
import { random } from 'lodash-es';
import { memo, useEffect } from 'react';
import { schema } from './config';
import * as Styled from './styles';
import * as SX from './sx';
import type { CreateListFormProps as Props } from './types';

export const CreateListForm = memo(
  ({ className, visibility, onSuccess, applyState }: Props) => {
    const notify = useNotify();
    const api = useCreateList();
    const type = useSearchTypeFromRoute();
    const { isPeoplePage } = usePageType();

    useEffect(() => {
      const isChanging = api.isLoading;
      applyState?.setValue(isChanging);
    }, [api]);

    const handleSubmit: FormOnSubmit = (data: YupFormData<typeof schema>) => {
      api
        .mutateAsync({
          type: LabelType.LIST,
          name: data.name,
          visibility,
          grouping:
            type === SearchType.People || isPeoplePage
              ? LabelGrouping.PEOPLE
              : LabelGrouping.COMPANY,
        })
        .then((payload) => {
          notify.complete(
            'Lists Created!',
            'Your New Lists Added.',
            random(1, 101),
            {
              icon: notificationIconMap.List,
            }
          );
          onSuccess?.(payload);
        });
    };

    return (
      <Styled.Wrapper
        className={classnames('CreateListForm', className)}
        sx={SX.root}
        scheme={schema}
        submitButtonProps={{
          children: 'Create List',
          size: 'medium',
          sx: SX.submitButton,
        }}
        loading={api.isLoading}
        actionsSx={{ paddingTop: 0 }}
        resetAfterSubmit
        onSubmit={handleSubmit}
      >
        <Styled.Input
          autoComplete="off"
          name="name"
          placeholder="Enter List Name"
          size="small"
        />
      </Styled.Wrapper>
    );
  }
);

CreateListForm.displayName = 'CreateListForm';
